import { INCORRECT_MASTER_KEY } from "common/crypto";
import {
  EMPLOYEE_MODEL_SCHEMA,
  EMPLOYEE_PARTS,
  OPTION_FIELDS,
  getDefaultModelValue,
  getOptionList,
  hasValue,
  sameId,
  toId,
  toSafeOption,
} from "./employeeModel";

export const assignIdByType = (type) => EMPLOYEE_PARTS.find(({ endpoint }) => endpoint === type)?.recordIdKey;

export function assignOption(type, value, data = {}) {
  const field = Object.keys(OPTION_FIELDS).find((key) => key === type || OPTION_FIELDS[key].idKey === type);
  if (!field) return value;
  const option = getOptionList(data, OPTION_FIELDS[field].listKeys).find((candidate) => sameId(candidate.id, toId(value)));
  return toSafeOption(option ?? value);
}

export function normalizeEmployeeOptions(employee, dropdownOptions = {}) {
  return Object.entries(OPTION_FIELDS).reduce((result, [field, config]) => {
    const currentValue = result[field];
    const value = hasValue(toId(currentValue)) ? currentValue : result[config.idKey];
    const option = getOptionList(dropdownOptions, config.listKeys).find((candidate) => sameId(candidate.id, toId(value)));
    return { ...result, [field]: toSafeOption(option ?? value) };
  }, { ...employee });
}

export function mergeEmployeePartResponse(employee, responseData, part) {
  if (!responseData) return employee;
  const { id: responseId, ...responseFields } = responseData;
  const normalized = part.fromDTO(responseData) || {};
  const relatedEmployeeId = normalized.employeeId ?? responseData.employeeId ?? responseData.employee?.employeeId ?? responseData.employee?.id;
  const employeeId =
    part.endpoint === "employee"
      ? normalized.employeeId ?? responseData.employeeId ?? responseId ?? employee.employeeId
      : employee.employeeId ?? relatedEmployeeId;
  const recordId = normalized[part.recordIdKey] ?? responseData[part.recordIdKey] ?? responseId ?? employee[part.recordIdKey];
  const tableId = part.endpoint === "employee" && !hasValue(employee.id) && hasValue(recordId) ? String(recordId) : employee.id;
  return {
    ...employee,
    ...responseFields,
    ...normalized,
    ...(hasValue(employeeId) ? { employeeId } : {}),
    ...(hasValue(recordId) ? { [part.recordIdKey]: recordId } : {}),
    ...(hasValue(tableId) ? { id: String(tableId) } : {}),
  };
}

export const getEmployeeRelationId = (record, part = null) => {
  if (!record) return null;
  const normalized = part?.fromDTO?.(record) || {};
  return normalized.employeeId ?? record.employeeId ?? record.employee?.employeeId ?? record.employee?.id ?? null;
};

const indexUniqueEmployeeRecords = (records, part, coreIds) => {
  const indexed = new Map();
  (records || []).forEach((record) => {
    const employeeId = getEmployeeRelationId(record, part);
    if (!hasValue(employeeId)) throw new Error(`${part.endpoint} record is missing employeeId`);
    const key = String(employeeId);
    if (!coreIds.has(key)) throw new Error(`${part.endpoint} record references unknown employeeId=${employeeId}`);
    if (indexed.has(key)) throw new Error(`Duplicate ${part.endpoint} record for employeeId=${employeeId}`);
    indexed.set(key, record);
  });
  return indexed;
};

export function compileEmployeesFromCollections(data = {}) {
  const [corePart, ...relatedParts] = EMPLOYEE_PARTS;
  const coreIds = new Set();
  let employees = (data[corePart.sourceKey] || []).map((record) => {
    const employee = mergeEmployeePartResponse({}, record, corePart);
    if (!hasValue(employee.employeeId)) throw new Error("Employee core record is missing employeeId");
    const key = String(employee.employeeId);
    if (coreIds.has(key)) throw new Error(`Duplicate employee record for employeeId=${employee.employeeId}`);
    coreIds.add(key);
    return employee;
  });

  relatedParts.forEach((part) => {
    const byEmployeeId = indexUniqueEmployeeRecords(data[part.sourceKey], part, coreIds);
    employees = employees.map((employee) => {
      const record = byEmployeeId.get(String(employee.employeeId));
      return record ? mergeEmployeePartResponse(employee, record, part) : employee;
    });
  });

  const absencesByEmployeeId = (data.employeesAbsences || []).reduce((groups, record) => {
    const employeeId = getEmployeeRelationId(record);
    if (!hasValue(employeeId)) throw new Error("employees-absence record is missing employeeId");
    const key = String(employeeId);
    if (!coreIds.has(key)) throw new Error(`employees-absence record references unknown employeeId=${employeeId}`);
    groups.set(key, [...(groups.get(key) || []), { ...record }]);
    return groups;
  }, new Map());

  return employees.map((employee) => {
    const employeeAbsences = absencesByEmployeeId.get(String(employee.employeeId)) || [];
    if (employeeAbsences.length !== 1) return { ...employee, employeeAbsences };
    const absenceFields = { ...employeeAbsences[0] };
    const absenceId = absenceFields.id;
    delete absenceFields.id;
    delete absenceFields.employeeId;
    delete absenceFields.employee;
    return {
      ...employee,
      ...absenceFields,
      ...(hasValue(absenceId) ? { employeeAbsencesId: absenceId } : {}),
      employeeAbsences,
    };
  });
}

export async function prepareLoadedEmployees(employees = [], dropdownOptions = {}, engine) {
  if (!engine?.decryptEmployee) throw new Error("Employee loading requires explicit crypto capabilities");
  const seen = new Set();
  const preparedEmployees = await Promise.all(
    employees.map(async (employee) => {
      if (!hasValue(employee?.employeeId)) throw new Error("Loaded employee is missing employeeId");
      const key = String(employee.employeeId);
      if (seen.has(key)) throw new Error(`Duplicate loaded employee for employeeId=${employee.employeeId}`);
      seen.add(key);
      const withDefaults = Object.entries(EMPLOYEE_MODEL_SCHEMA).reduce(
        (result, [field, { type }]) =>
          result[field] === undefined ? { ...result, [field]: getDefaultModelValue(field, type) } : result,
        { ...employee }
      );
      const normalized = normalizeEmployeeOptions(withDefaults, dropdownOptions);
      const decryptedName = normalized.hash
        ? await engine.decryptEmployee(normalized.hash)
        : String(normalized.nameOrNum ?? "").trim();
      const decryptionFailed = decryptedName === INCORRECT_MASTER_KEY;
      return {
        ...normalized,
        id: String(normalized.id ?? normalized.employeeId),
        nameOrNum: decryptionFailed ? "" : decryptedName,
        errors: decryptionFailed ? { nameOrNum: INCORRECT_MASTER_KEY } : {},
        decryptionFailed,
        showAllSpecXFunc: normalized.showAllSpecXFunc ?? false,
      };
    })
  );
  return preparedEmployees.sort((left, right) => String(left.nameOrNum).localeCompare(String(right.nameOrNum)));
}
